import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft, Save } from "lucide-react"

import InputError from "@/components/input-error" // Assuming this component exists for displaying errors
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/app-layout"
import { proxyUrlPath, proxyUrlsPath } from "@/routes" // Assuming proxyUrlPath is for the show page
import type { BreadcrumbItem, Errors, ProxyUrl } from "@/types"

interface ProxyUrlEditProps {
  proxy_url: ProxyUrl
  errors?: Errors // For displaying validation errors from the server
}

const PRIORITY_OPTIONS = [
  { value: "0", label: "Default (0)" },
  { value: "10", label: "High (10)" },
  // Add more options if the application supports a wider range of priorities
]

export default function ProxyUrlEdit({
  proxy_url,
  errors: serverErrors,
}: ProxyUrlEditProps) {
  const { data, setData, put, processing, errors } = useForm({
    url: proxy_url.url ?? "",
    provider: proxy_url.provider ?? "",
    priority: proxy_url.priority ?? 0,
    // expires_at: proxy_url.expires_at || "" // Add if expiration field is implemented
  })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Proxy URLs",
      href: proxyUrlsPath(),
    },
    {
      title: proxy_url.url, // Or a truncated version
      href: proxyUrlPath(proxy_url.id), // Link to the show page
    },
    {
      title: "Edit",
      href: proxyUrlPath(proxy_url.id) + "/edit", // current page
    },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    put(proxyUrlPath(proxy_url.id), {
      // Using PUT to the resource URL for update
      // onSuccess: () => reset(), // Or redirect, handled by controller
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Proxy URL: ${proxy_url.url}`} />

      <div className="flex h-full flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="flex items-center">
          <Link href={proxyUrlPath(proxy_url.id)}>
            <Button variant="outline" size="icon" className="mr-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            Edit Proxy URL
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Proxy URL Details</CardTitle>
              <CardDescription>
                Update the details for this proxy URL.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="url">Proxy URL</Label>
                <Input
                  id="url"
                  placeholder="http://proxy.example.com:8080"
                  value={data.url}
                  onChange={(e) => setData("url", e.target.value)}
                  required
                  className={errors.url ? "border-red-500" : ""}
                />
                <InputError message={errors.url ?? serverErrors?.url?.[0]} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Input
                  id="provider"
                  placeholder="Enter proxy provider name"
                  value={data.provider}
                  onChange={(e) => setData("provider", e.target.value)}
                  required
                  className={errors.provider ? "border-red-500" : ""}
                />
                <InputError
                  message={errors.provider ?? serverErrors?.provider?.[0]}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={data.priority.toString()}
                  onValueChange={(value) =>
                    setData("priority", parseInt(value))
                  }
                >
                  <SelectTrigger
                    className={errors.priority ? "border-red-500" : ""}
                  >
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  message={errors.priority ?? serverErrors?.priority?.[0]}
                />
              </div>

              {/*
              // Uncomment if expires_at is implemented
              <div className="space-y-2">
                <Label htmlFor="expires_at">Expiration Date (Optional)</Label>
                <Input
                  id="expires_at"
                  type="date" // Or datetime-local
                  value={data.expires_at}
                  onChange={(e) => setData("expires_at", e.target.value)}
                  className={errors.expires_at ? "border-red-500" : ""}
                />
                <InputError message={errors.expires_at || serverErrors?.expires_at?.[0]} />
              </div>
              */}
            </CardContent>
            <CardFooter className="flex justify-end space-x-2 border-t px-6 py-4">
              <Link href={proxyUrlPath(proxy_url.id)}>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={processing}>
                <Save className="mr-2 h-4 w-4" />
                {processing ? "Saving..." : "Save Changes"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </div>
    </AppLayout>
  )
}
